'use client';
import React, { createContext, useCallback, useContext, useEffect } from 'react';

import type { DomainContext } from '@nutralab/api';
import { formatDate } from '@nutralab/common';

import { TIME_ZONE_COOKIE, TIME_ZONE_COOKIE_MAX_AGE_SECONDS } from '@/shared/constants';

interface SiteConfigContextValue {
    config: DomainContext;
    timeZone: string;
}

const SiteConfigContext = createContext<SiteConfigContextValue | null>(null);

interface SiteConfigProviderProps {
    config: DomainContext;
    /** Time zone the server rendered dates in (`getSiteTimeZone`); the browser formats in the same one. */
    timeZone: string;
    children: React.ReactNode;
}

/**
 * Shares the site context loaded on the server (region, language, contacts, categories) with client components
 * (`useSiteConfig`, `useFormatDate`), and saves the browser's time zone for the next server render.
 */
export function SiteConfigProvider({ config, timeZone, children }: SiteConfigProviderProps): React.ReactElement {
    useEffect(() => {
        const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTimeZone === timeZone) {
            return;
        }
        // Takes effect from the next page: this one keeps the server's zone, so the text doesn't change after load.
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${TIME_ZONE_COOKIE}=${encodeURIComponent(browserTimeZone)}; Path=/; Max-Age=${TIME_ZONE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
    }, [timeZone]);

    return <SiteConfigContext.Provider value={{ config, timeZone }}>{children}</SiteConfigContext.Provider>;
}

function useSiteConfigContext(): SiteConfigContextValue {
    const ctx = useContext(SiteConfigContext);
    if (!ctx) {
        throw new Error('useSiteConfig must be used within SiteConfigProvider');
    }
    return ctx;
}

export function useSiteConfig(): DomainContext {
    return useSiteConfigContext().config;
}

/** Formats a date in the site language and the time zone the server rendered with (same text on both sides). */
export function useFormatDate(): (value: string | Date) => string {
    const { config, timeZone } = useSiteConfigContext();
    const locale = config.current_language.code;
    return useCallback((value: string | Date) => formatDate(value, { locale, timeZone }), [locale, timeZone]);
}
